import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { alpha } from "@mui/material/styles";
import {
  Grid,
  Stack,
  Box,
  CircularProgress,
  Typography,
  Radio,
  Divider,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import axios from "axios";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { MapPin, Phone, Mail, FileText, Info, Calendar, Check, User } from "lucide-react";
import PageProtectedOnboarding from "../../../components/Page/PageProtectedOnboarding";
import { pool, API_URL } from "../../../constants";
import { useAuth } from "../../../provider";
import { useTranslation } from "../../../language";
import OnbSteps from "../../../components/Steps/OnbSteps";
import ButtonForm from "../../../components/Button/ButtonForm";
import FormAlert from "../../../components/Alert/FormAlert";
import DateInput from "../../../components/Forms/DateInput";
import SelectInputNew from "../../../components/Forms/SelectInputNew";
import CustomDialogSmall from "../../../components/Dialog/DiaCompSmall";

function cardSx(theme, accentColor) {
  return {
    border: '1px solid',
    borderRadius: '16px !important', // !important needed for Accordion: MUI's :first-of-type/:last-of-type defaults outrank a plain sx value
    overflow: 'hidden',
    borderLeft: `10px solid ${accentColor}`,
    ...(theme.palette.mode === 'light'
      ? { bgcolor: 'grey.C50', borderColor: 'grey.C900' }
      : { bgcolor: 'greydark.C900', borderColor: 'greydark.C400',
          backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.6)' }),
  };
}

function LabRow({ lab, selected, onSelect, onInfo }) {
  const theme = useTheme();
  return (
    <Box
      onClick={onSelect}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.5,
        py: 0.5, px: 2, borderRadius: 4, overflow: 'hidden', cursor: 'pointer',
        border: '1px solid',
        borderColor: selected
          ? theme.palette.primary.main
          : (theme.palette.mode === 'light' ? 'grey.C900' : 'greydark.C400'),
        bgcolor: selected
          ? (theme.palette.mode === 'light' ? 'grey.C100' : 'greydark.C900')
          : (theme.palette.mode === 'light' ? 'grey.C50' : 'greydark.C900'),
        transition: 'border-color 0.15s, background-color 0.15s',
      }}
    >
      <Radio checked={selected} onChange={onSelect} size="small" sx={{ py: 0.5 }} />
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={400}>{lab.name}</Typography>
      </Box>
      <Tooltip title="Info" placement="top">
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); onInfo(); }}
          sx={{ flexShrink: 0, '&:hover': { transform: 'scale(1.2)' } }}
        >
          <Info size={18} color={theme.palette.secondary.dark} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

function ScheduledSummary({ labName, visitDate, visitTime, gender, birthdate, accentColor, successText }) {
  const theme = useTheme();
  const { t } = useTranslation();
  return (
    <Box sx={{ ...cardSx(theme, accentColor), p: 3 }}>
      {successText && (
        <FormAlert severity="success" message={successText} sx={{ mb: 2 }} />
      )}
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box
          sx={{
            width: 44, height: 44, borderRadius: 1.5, flexShrink: 0,
            bgcolor: theme.palette.mode === 'light' ? 'grey.C100' : 'greydark.C900',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <MapPin size={20} color={accentColor} />
        </Box>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={400}>{labName}</Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Calendar size={16} color={accentColor} />
            <Typography variant="subtitle2" color="text.secondary" fontWeight={500}>
              {visitDate} · {visitTime}
            </Typography>
          </Stack>
        </Box>
        <Check size={22} strokeWidth={2.5} color={theme.palette.traffic.green} />
      </Stack>

      {(gender || birthdate) && (
        <>
          <Divider sx={{ my: 2 }} />
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 44, height: 44, borderRadius: 1.5, flexShrink: 0,
                bgcolor: theme.palette.mode === 'light' ? 'grey.C100' : 'greydark.C900',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <User size={20} color={accentColor} />
            </Box>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" color="text.secondary" fontWeight={500}>
                {gender ? t(GENDER_LABEL_KEYS[gender]) : '—'} · {birthdate || '—'}
              </Typography>
            </Box>
            <Check size={22} strokeWidth={2.5} color={theme.palette.traffic.green} />
          </Stack>
        </>
      )}
    </Box>
  );
}

const TIME_FORMAT = "HH:mm";

// Themed time selector — uses theme.jsx colors for the picker popup
// (a native <input type="time"> clock icon can't be recolored, MUI's TimePicker can)
function ThemedTimeInput({ label, value, onChange, error, helperText }) {
  const theme = useTheme();
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <TimePicker
        label={label}
        ampm={false}
        value={value ? dayjs(value, TIME_FORMAT) : null}
        onChange={(newValue) =>
          onChange(newValue && newValue.isValid() ? newValue.format(TIME_FORMAT) : "")
        }
        slotProps={{
          textField: {
            fullWidth: true,
            size: "small",
            margin: "normal",
            error,
            helperText,
          },
          popper: {
            sx: {
              '& .MuiPaper-root': {
                backgroundColor: theme.palette.grey.C200,
                borderRadius: 6,
              },
              '& .MuiClockNumber-root': {
                color: theme.palette.greydark.C900,
                fontWeight: 600,
              },
              '& .MuiClockNumber-root.Mui-selected': {
                color: theme.palette.common.white,
              },
              '& .MuiClock-pin, & .MuiClockPointer-root': {
                backgroundColor: theme.palette.grey.C500,
              },
              '& .MuiClockPointer-thumb': {
                backgroundColor: theme.palette.grey.C500,
                borderColor: theme.palette.grey.C500,
              },
              '& .MuiDialogActions-root .MuiButton-root': {
                color: theme.palette.greydark.C900,
              },
            },
          },
        }}
      />
    </LocalizationProvider>
  );
}

const getValidationSchema = (t) =>
  Yup.object({
    laboratory_id: Yup.number().required(t("val_required")),
    visit_date: Yup.date()
      .min(new Date(), t("val_date_past"))
      .required(t("val_required")),
    visit_time: Yup.string().required(t("val_time_req")),
    gender: Yup.string()
      .oneOf(["M", "F", "O"], t("val_gender_invalid"))
      .required(t("val_required")),
    birthdate: Yup.date()
      .max(new Date(), t("val_birthday_future"))
      .required(t("val_required")),
  });

const GENDER_LABEL_KEYS = { M: "onb_subscr245", F: "onb_subscr246", O: "onb_subscr247" };

export default function FirstLabvisit() {
  const { state, handleLogin } = useAuth();
  const { t, changeLanguage } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();

  const [labVisit, setLabVisit] = useState(null);
  const [visitLoading, setVisitLoading] = useState(true);
  const [visitError, setVisitError] = useState(null);

  const [labs, setLabs] = useState([]);
  const [labsLoading, setLabsLoading] = useState(true);
  const [errorLabs, setErrorLabs] = useState(null);
  const [infoLab, setInfoLab] = useState(null);

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [scheduledSummary, setScheduledSummary] = useState(null);

  useEffect(() => {
    if (state.user?.language) {
      changeLanguage(state.user.language);
    }
  }, [state.user?.language]);

  useEffect(() => {
    async function fetchVisit() {
      try {
        const response = await axios.get(`${API_URL}/api/user-lab-visits/`);
        const visits = Array.isArray(response.data) ? response.data : [];
        const visit = visits.find(
          (v) => v.subscription_period === 1 && v.visit_number === 1
        );
        setLabVisit(visit ?? null);
      } catch (err) {
        console.error("Failed to fetch lab visit:", err);
        setVisitError(t("onb_quest_err_no_visit"));
      } finally {
        setVisitLoading(false);
      }
    }
    fetchVisit();
  }, []);

  useEffect(() => {
    async function fetchLabs() {
      try {
        const response = await axios.get(`${API_URL}/api/laboratories/`);
        setLabs(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        console.error("Failed to fetch labs:", err);
        setErrorLabs(t("onb_lab_err_load"));
      } finally {
        setLabsLoading(false);
      }
    }
    fetchLabs();
  }, []);

  const alreadyCompleted = Boolean(
    labVisit?.laboratory && labVisit?.visit_date && labVisit?.visit_time &&
    state.user?.gender && state.user?.birthdate
  );

  const handleSubmit = async (values, formikHelpers) => {
    const { laboratory_id, visit_date, visit_time, gender, birthdate } = values;
    setErrorMessage(null);
    setLoading(true);
    try {
      const [labVisitRes, profileRes] = await Promise.all([
        axios.patch(`${API_URL}/api/user-lab-visits/${labVisit.id}/`, {
          laboratory_id, visit_date, visit_time, state: 3,
        }),
        axios.put(`${API_URL}/api/auth/update_newprofile/`, {
          gender, birthdate,
        }),
      ]);

      if ([200, 201].includes(labVisitRes.status) && [200, 201].includes(profileRes.status)) {
        handleLogin({ user: { ...state.user, gender, birthdate }, token: state.token });

        await axios.patch(`${API_URL}/api/onboarding/status/`, {
          onboarding_status: 3,
        });

        const lab = labs.find((l) => String(l.id) === String(laboratory_id));
        setScheduledSummary({
          labName: lab?.name,
          visit_date,
          visit_time,
          gender,
          birthdate,
        });
        setSubmitted(true);
        setTimeout(() => {
          navigate("/AddonSelection", { replace: true });
        }, 1500);
      }
    } catch (err) {
      if (err.response?.data) {
        const apiErrors = err.response.data;
        Object.keys(apiErrors).forEach((field) => {
          const message = Array.isArray(apiErrors[field])
            ? apiErrors[field].join(" ")
            : apiErrors[field];
          formikHelpers.setFieldError(field, message);
        });
        setErrorMessage(t("val_form_fix"));
      } else {
        setErrorMessage(t("val_server_error"));
      }
    } finally {
      setLoading(false);
      formikHelpers.setSubmitting(false);
    }
  };

  const accentLeft = theme.palette.tertiary.C300;
  const accentRight = theme.palette.quartiary.C300;

  const initialValues = {
    laboratory_id: labVisit?.laboratory?.id ?? "",
    visit_date: labVisit?.visit_date ?? "",
    visit_time: labVisit?.visit_time ?? "07:30",
    gender: state.user?.gender ?? "",
    birthdate: state.user?.birthdate ?? "",
  };

  return (
    <PageProtectedOnboarding
      headerImage={pool}
      brandtext={t("onbsub_brandtext")}
      content={
        <>
          <Grid container sx={{ mb: 5, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Grid size={12} sx={{ width: '100%', maxWidth: 900, px: { xs: 1, sm: 2 }, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <OnbSteps stepNumber={1} />
            </Grid>
          </Grid>

          {visitLoading || labsLoading ? (
            <Grid container sx={{ justifyContent: 'center', my: 6 }}>
              <CircularProgress />
            </Grid>
          ) : !labVisit ? (
            <Grid container sx={{ justifyContent: 'center', my: 6, px: { xs: 3, sm: 0 } }}>
              <Typography variant="subtitle1" color="text.secondary" textAlign="center">
                {visitError || t("onb_quest_err_no_visit")}
              </Typography>
            </Grid>
          ) : (
            <Grid container spacing={4} sx={{ width: '100%', maxWidth: 900, px: { xs: 1, sm: 2 }, mx: 'auto' }}>
              <Grid size={12}>
                <Box sx={{ px: 3 }}>
                  {submitted || alreadyCompleted ? (
                    <ScheduledSummary
                      labName={scheduledSummary?.labName ?? labVisit.laboratory?.name}
                      visitDate={scheduledSummary?.visit_date ?? labVisit.visit_date}
                      visitTime={scheduledSummary?.visit_time ?? labVisit.visit_time}
                      gender={scheduledSummary?.gender ?? state.user?.gender}
                      birthdate={scheduledSummary?.birthdate ?? state.user?.birthdate}
                      accentColor={accentRight}
                      successText={submitted ? t("onb_lab_success") : null}
                    />
                  ) : (
                    <Formik
                      initialValues={initialValues}
                      validationSchema={getValidationSchema(t)}
                      enableReinitialize
                      onSubmit={handleSubmit}
                    >
                      {({ values, setFieldValue, isSubmitting, touched, errors }) => (
                        <Form>
                          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 2 }}>
                            {t("onb_lab_accordion_hint")}
                          </Typography>

                          <Accordion
                            disableGutters
                            elevation={0}
                            sx={{
                              ...cardSx(theme, accentLeft),
                              mb: 2,
                              '&:before': { display: 'none' },
                            }}
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon />}
                              sx={{
                                px: 2.5,
                                py: 1,
                                '& .MuiAccordionSummary-content': {
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 1,
                                  my: 1,
                                },
                              }}
                            >
                              <Stack direction="row" spacing={2} alignItems="center">
                                <Box
                                  sx={{
                                    flexShrink: 0,
                                    width: 28, height: 28,
                                    borderRadius: '50%',
                                    border: `2px solid ${theme.palette.mode === 'light' ? theme.palette.greydark.C400 : theme.palette.greydark.C100}`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  }}
                                >
                                  <Typography
                                    variant="number4"
                                    sx={{
                                      color: theme.palette.mode === 'light' ? theme.palette.greydark.C400 : theme.palette.greydark.C100,
                                      fontWeight: 400, fontSize: '0.75rem', lineHeight: 1,
                                    }}
                                  >
                                    1
                                  </Typography>
                                </Box>
                                <Typography variant="subtitle1" fontWeight={400}>{t("onb_lab_select_heading")}</Typography>
                              </Stack>
                              <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
                                <Typography component="li" variant="body2" color="text.secondary">
                                  {t("onb_lab0b")}
                                </Typography>
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ px: 2.5, pb: 2 }}>
                              {errorLabs ? (
                                <FormAlert severity="error" message={errorLabs} />
                              ) : (
                                <Stack spacing={1.5}>
                                  {labs.map((lab) => (
                                    <LabRow
                                      key={lab.id}
                                      lab={lab}
                                      selected={String(values.laboratory_id) === String(lab.id)}
                                      onSelect={() => setFieldValue("laboratory_id", lab.id)}
                                      onInfo={() => setInfoLab(lab)}
                                    />
                                  ))}
                                </Stack>
                              )}
                            </AccordionDetails>
                          </Accordion>

                          <Accordion
                            disableGutters
                            elevation={0}
                            sx={{
                              ...cardSx(theme, accentRight),
                              mb: 2,
                              '&:before': { display: 'none' },
                            }}
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon />}
                              sx={{
                                px: 2.5,
                                py: 1,
                                '& .MuiAccordionSummary-content': {
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 1,
                                  my: 1,
                                },
                              }}
                            >
                              <Stack direction="row" spacing={2} alignItems="center">
                                <Box
                                  sx={{
                                    flexShrink: 0,
                                    width: 28, height: 28,
                                    borderRadius: '50%',
                                    border: `2px solid ${theme.palette.mode === 'light' ? theme.palette.greydark.C400 : theme.palette.greydark.C100}`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  }}
                                >
                                  <Typography
                                    variant="number4"
                                    sx={{
                                      color: theme.palette.mode === 'light' ? theme.palette.greydark.C400 : theme.palette.greydark.C100,
                                      fontWeight: 400, fontSize: '0.75rem', lineHeight: 1,
                                    }}
                                  >
                                    2
                                  </Typography>
                                </Box>
                                <Typography variant="subtitle1" fontWeight={400}>{t("onb_lab_datetime_heading")}</Typography>
                              </Stack>
                              <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
                                <Typography component="li" variant="body2" color="text.secondary">
                                  {t("onb_lab_hours_note")}
                                </Typography>
                                <Typography component="li" variant="body2" color="text.secondary">
                                  {t("onb_lab_datetime_change_note")}
                                </Typography>
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ px: 2.5, pb: 2 }}>
                              <DateInput label={t("onb_lab3")} name="visit_date" />
                              <ThemedTimeInput
                                label={t("onb_lab4")}
                                value={values.visit_time}
                                onChange={(newValue) => setFieldValue("visit_time", newValue)}
                                error={touched.visit_time && Boolean(errors.visit_time)}
                                helperText={touched.visit_time && errors.visit_time ? errors.visit_time : ''}
                              />
                            </AccordionDetails>
                          </Accordion>

                          <Accordion
                            disableGutters
                            elevation={0}
                            sx={{
                              ...cardSx(theme, accentLeft),
                              mb: 2,
                              '&:before': { display: 'none' },
                            }}
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon />}
                              sx={{
                                px: 2.5,
                                py: 1,
                                '& .MuiAccordionSummary-content': {
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 1,
                                  my: 1,
                                },
                              }}
                            >
                              <Stack direction="row" spacing={2} alignItems="center">
                                <Box
                                  sx={{
                                    flexShrink: 0,
                                    width: 28, height: 28,
                                    borderRadius: '50%',
                                    border: `2px solid ${theme.palette.mode === 'light' ? theme.palette.greydark.C400 : theme.palette.greydark.C100}`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  }}
                                >
                                  <Typography
                                    variant="number4"
                                    sx={{
                                      color: theme.palette.mode === 'light' ? theme.palette.greydark.C400 : theme.palette.greydark.C100,
                                      fontWeight: 400, fontSize: '0.75rem', lineHeight: 1,
                                    }}
                                  >
                                    3
                                  </Typography>
                                </Box>
                                <Typography variant="subtitle1" fontWeight={400}>{t("onb_lab_personal_heading")}</Typography>
                              </Stack>
                              <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
                                <Typography component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                  {t("onb_lab_personal_note1")}
                                </Typography>
                                <Typography component="li" variant="body2" color="text.secondary">
                                  {t("onb_lab_personal_note2")}
                                </Typography>
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ px: 2.5, pb: 2 }}>
                              <SelectInputNew
                                label={t("onb_subscr24")}
                                name="gender"
                                options={[
                                  { value: "", label: t("onb_subscr244") },
                                  { value: "M", label: t("onb_subscr245") },
                                  { value: "F", label: t("onb_subscr246") },
                                  { value: "O", label: t("onb_subscr247") },
                                ]}
                              />
                              <DateInput label={t("onb_subscr25")} name="birthdate" />
                            </AccordionDetails>
                          </Accordion>

                          <Box sx={{ ...cardSx(theme, accentRight), p: 3 }}>
                            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                              <Box
                                sx={{
                                  flexShrink: 0,
                                  width: 28, height: 28,
                                  borderRadius: '50%',
                                  border: `2px solid ${theme.palette.mode === 'light' ? theme.palette.greydark.C400 : theme.palette.greydark.C100}`,
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}
                              >
                                <Typography
                                  variant="number4"
                                  sx={{
                                    color: theme.palette.mode === 'light' ? theme.palette.greydark.C400 : theme.palette.greydark.C100,
                                    fontWeight: 400, fontSize: '0.75rem', lineHeight: 1,
                                  }}
                                >
                                  4
                                </Typography>
                              </Box>
                              <Typography variant="subtitle1" fontWeight={400}>{t("onb_lab_review_heading")}</Typography>
                            </Stack>

                            <Divider sx={{ mb: 2 }} />

                            <Box
                              sx={{
                                bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.05 : 0.12),
                                borderRadius: 3,
                                p: 2,
                              }}
                            >
                              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                                <Box
                                  sx={{
                                    width: 44, height: 44, borderRadius: 1.5, flexShrink: 0,
                                    bgcolor: theme.palette.mode === 'light' ? 'grey.C100' : 'greydark.C900',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  }}
                                >
                                  <MapPin size={20} color={accentRight} />
                                </Box>
                                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                  <Typography variant="subtitle1" fontWeight={400}>
                                    {labs.find((l) => String(l.id) === String(values.laboratory_id))?.name || t("onb_lab5")}
                                  </Typography>
                                  <Stack direction="row" alignItems="center" spacing={1}>
                                    <Calendar size={16} color={accentRight} />
                                    <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
                                      {values.visit_date || t("onb_lab3")} · {values.visit_time || t("onb_lab4")}
                                    </Typography>
                                  </Stack>
                                </Box>
                              </Stack>

                              <Stack direction="row" spacing={1.5} alignItems="center">
                                <Box
                                  sx={{
                                    width: 44, height: 44, borderRadius: 1.5, flexShrink: 0,
                                    bgcolor: theme.palette.mode === 'light' ? 'grey.C100' : 'greydark.C900',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  }}
                                >
                                  <User size={20} color={accentRight} />
                                </Box>
                                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                  <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
                                    {values.gender ? t(GENDER_LABEL_KEYS[values.gender]) : t("onb_subscr24")} · {values.birthdate || t("onb_subscr25")}
                                  </Typography>
                                </Box>
                              </Stack>
                            </Box>

                              <Divider sx={{ my: 2 }} />

                              {loading ? (
                                <Grid container sx={{ justifyContent: 'center', my: 2 }}>
                                  <CircularProgress size={24} />
                                </Grid>
                              ) : (
                                <ButtonForm
                                  name={t("onb_labb")}
                                  disabled={isSubmitting || loading}
                                />
                              )}

                              {errorMessage && <FormAlert severity="error" message={errorMessage} sx={{ mt: 2 }} />}
                          </Box>
                        </Form>
                      )}
                    </Formik>
                  )}
                </Box>
              </Grid>
            </Grid>
          )}

          {infoLab && (
            <CustomDialogSmall
              open
              onClose={() => setInfoLab(null)}
              title={infoLab.name}
              content={
                <Stack spacing={2} sx={{ mt: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                    <MapPin size={22} color={theme.palette.primary.main} style={{ marginTop: 2, flexShrink: 0 }} />
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 400 }}>
                        {t("onb_lab_info_address")}
                      </Typography>
                      <Typography variant="subtitle1" sx={{ fontWeight: 400 }}>
                        {[
                          infoLab.details?.address?.street,
                          infoLab.details?.address?.city,
                          infoLab.details?.address?.postal_code,
                          infoLab.details?.address?.country,
                        ].filter(Boolean).join(", ")}
                      </Typography>
                    </Box>
                  </Box>

                  {infoLab.details?.phone && (
                    <>
                      <Divider />
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Phone size={22} color={theme.palette.primary.main} style={{ flexShrink: 0 }} />
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 400 }}>
                            {t("onb_lab_info_phone")}
                          </Typography>
                          <Typography variant="subtitle1" sx={{ fontWeight: 400 }}>
                            {infoLab.details.phone}
                          </Typography>
                        </Box>
                      </Box>
                    </>
                  )}

                  {infoLab.details?.email && (
                    <>
                      <Divider />
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Mail size={22} color={theme.palette.primary.main} style={{ flexShrink: 0 }} />
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 400 }}>
                            {t("onb_lab_info_email")}
                          </Typography>
                          <Typography variant="subtitle1" sx={{ fontWeight: 400 }}>
                            {infoLab.details.email}
                          </Typography>
                        </Box>
                      </Box>
                    </>
                  )}

                  {infoLab.details?.notes && (
                    <>
                      <Divider />
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                        <FileText size={22} color={theme.palette.primary.main} style={{ marginTop: 2, flexShrink: 0 }} />
                        <Box>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 400 }}>
                            {t("onb_lab_info_notes")}
                          </Typography>
                          <Typography variant="subtitle1" sx={{ fontWeight: 400 }}>
                            {infoLab.details.notes}
                          </Typography>
                        </Box>
                      </Box>
                    </>
                  )}
                </Stack>
              }
            />
          )}
        </>
      }
    />
  );
}
